import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useRecoilValue } from 'recoil';
import { ActiveTeamAtom, TeamTreeVersionAtom } from '../../recoil/teamAtoms';
import { getTeamTree } from '../../utils/teamApi';
import { formatNumber } from '../../../utils/formatNumber';
import AppHeader from '../AppHeader';

// ===== styled =====
const Page = styled.div`
  min-height: 100vh;
  background: ${p => p.theme.bg};
  display: flex;
  flex-direction: column;
`;

const Body = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 280px 1fr 320px;
  gap: 16px;
  padding: 20px;
  max-width: 1600px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  @media screen and (max-width: 1024px) {
    grid-template-columns: 1fr;
    padding: 12px;
    gap: 12px;
    overflow: visible;
  }
`;

const Column = styled.section`
  background: ${p => p.theme.bgCard};
  border: 1px solid ${p => p.theme.border};
  border-radius: ${p => p.theme.radius};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: calc(100vh - 110px);

  @media screen and (max-width: 1024px) {
    max-height: none;
    /* 모바일에서 '진행 단계' 좌측 컬럼은 숨김 — 고객은 항목 선택/출력만 */
    ${p => p.$hideOnMobile && `
      display: none;
    `}
  }
`;

const ColHeader = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.bgInput};
`;

const ColTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: ${p => p.theme.text};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TeamBadge = styled.span`
  font-size: 11px;
  background: ${p => p.theme.accent};
  color: #fff;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 600;
`;

const ColBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 14px 16px;
`;

const ColFooter = styled.div`
  padding: 12px 16px;
  border-top: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.bgInput};
`;

// 좌측: progress
const ProgressItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px dashed ${p => p.theme.border};
  cursor: ${p => p.$clickable ? 'pointer' : 'default'};

  &:last-child { border-bottom: none; }

  &:hover {
    background: ${p => p.$clickable ? p.theme.bgHover : 'transparent'};
  }

  .num {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: ${p => p.$active ? p.theme.accent : p.$done ? p.theme.success : p.theme.bgInput};
    color: ${p => (p.$active || p.$done) ? '#fff' : p.theme.textMuted};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .body {
    flex: 1;
    min-width: 0;
  }

  .title {
    font-size: 12px;
    font-weight: 700;
    color: ${p => p.theme.text};
    margin-bottom: 2px;
  }

  .selected {
    font-size: 11px;
    color: ${p => p.theme.accent};
    font-weight: 600;
  }

  .pending {
    font-size: 11px;
    color: ${p => p.theme.textMuted};
  }
`;

// 중앙: wizard
const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 14px;
  font-size: 12px;
`;

const BackBtn = styled.button`
  background: ${p => p.theme.bgCard};
  border: 1px solid ${p => p.theme.border};
  color: ${p => p.theme.textSecondary};
  padding: 4px 10px;
  border-radius: ${p => p.theme.radiusSm};
  font-size: 11px;
  cursor: pointer;
  margin-right: 6px;
  font-weight: 600;

  &:hover:not(:disabled) { background: ${p => p.theme.bgHover}; color: ${p => p.theme.text}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const Crumb = styled.span`
  cursor: ${p => p.$active ? 'default' : 'pointer'};
  color: ${p => p.$active ? p.theme.text : p.theme.accent};
  font-weight: ${p => p.$active ? 700 : 500};
  padding: 2px 6px;
  border-radius: 4px;

  &:hover { background: ${p => p.$active ? 'transparent' : p.theme.accentLight}; }
`;

const Sep = styled.span`
  color: ${p => p.theme.textMuted};
  font-size: 11px;
`;

const CurrentCard = styled.div`
  background: ${p => p.theme.bgCard};
  border: 2px solid ${p => p.theme.borderSelected};
  border-radius: ${p => p.theme.radius};
  padding: 16px 18px;
  margin-bottom: 14px;
`;

const CurrentTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 17px;
  font-weight: 700;
  color: ${p => p.theme.text};
  flex-wrap: wrap;
`;

const TypeBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid ${p => p.$multi ? p.theme.success : p.theme.accent};
  color: ${p => p.$multi ? p.theme.success : p.theme.accent};
`;

const Desc = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  color: ${p => p.theme.textMuted};
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${p => p.theme.text};
  margin: 14px 0 10px;

  small {
    color: ${p => p.theme.textMuted};
    font-size: 11px;
    font-weight: 500;
    margin-left: 6px;
  }
`;

const ItemGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 10px;

  @media screen and (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

const ItemCard = styled.button`
  text-align: left;
  background: ${p => p.$selected ? p.theme.bgSelected : p.theme.bgCard};
  border: 2px solid ${p => p.$selected ? p.theme.borderSelected : p.theme.border};
  border-radius: ${p => p.theme.radius};
  padding: 14px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: ${p => p.theme.transition};
  position: relative;

  &:hover {
    border-color: ${p => p.theme.accent};
    box-shadow: ${p => p.theme.shadowCardHover};
  }
`;

const ItemNameRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
`;

const CardImage = styled.div`
  width: 100%;
  aspect-ratio: 16 / 10;
  border-radius: 8px;
  background: ${p => p.theme.bgInput};
  border: 1px solid ${p => p.theme.border};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  span.placeholder {
    font-size: 12px;
    color: ${p => p.theme.textMuted};
    font-weight: 600;
  }
`;

const ItemTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${p => p.theme.text};
`;

const ItemPrice = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${p => p.theme.accent};
  white-space: nowrap;
`;

const ItemDesc = styled.div`
  font-size: 12px;
  color: ${p => p.theme.textMuted};
  line-height: 1.5;
  min-height: 30px;
`;

const ItemFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 8px;
  border-top: 1px solid ${p => p.theme.border};
  font-size: 11px;
  color: ${p => p.theme.textMuted};
  font-weight: 600;
`;

const NextHint = styled.span`
  color: ${p => p.theme.accent};
`;

const SelectionDot = styled.span`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${p => p.theme.accent};
`;

// 우측: summary
const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 12px;
  background: ${p => p.theme.bgInput};
  border-radius: ${p => p.theme.radiusSm};
  margin-bottom: 6px;
  font-size: 13px;

  .name {
    color: ${p => p.theme.text};
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-right: 8px;
  }

  .price {
    color: ${p => p.theme.accent};
    font-weight: 700;
    white-space: nowrap;
  }
`;

const TotalCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-top: 12px;
  margin-top: 8px;
  border-top: 2px solid ${p => p.theme.accent};

  .label {
    font-size: 13px;
    font-weight: 600;
    color: ${p => p.theme.text};
  }
  .total {
    font-size: 24px;
    font-weight: 800;
    color: ${p => p.theme.accent};
  }
`;

const VatNote = styled.div`
  text-align: right;
  font-size: 11px;
  color: ${p => p.theme.textMuted};
  margin-top: 2px;
`;

const ResetButton = styled.button`
  width: 100%;
  padding: 9px;
  border: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.bgCard};
  color: ${p => p.theme.textSecondary};
  border-radius: ${p => p.theme.radiusSm};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 8px;

  &:hover { background: ${p => p.theme.bgHover}; color: ${p => p.theme.text}; }
`;

const Empty = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: ${p => p.theme.textMuted};
  font-size: 13px;
  background: ${p => p.theme.bgCard};
  border: 1px dashed ${p => p.theme.border};
  border-radius: ${p => p.theme.radius};
`;

const Notice = styled.div`
  background: ${p => p.theme.accentLight};
  color: ${p => p.theme.accent};
  font-size: 12px;
  padding: 10px 12px;
  border-radius: ${p => p.theme.radiusSm};
  margin-bottom: 12px;
  line-height: 1.5;
`;

// ===== Component =====
export default function TeamWizardApp() {
  const activeTeam = useRecoilValue(ActiveTeamAtom);
  const treeVersion = useRecoilValue(TeamTreeVersionAtom);
  const [tree, setTree] = useState({ categories: [], items: [] });
  const priceUnit = activeTeam?.price_unit || '만원';
  const [loading, setLoading] = useState(false);
  // selected: { [categoryId]: item OR [items array for multi-select] }
  const [selected, setSelected] = useState({});
  const [drillPath, setDrillPath] = useState([]);

  // 팀 객체가 아니라 id 에 매단다. ActiveTeamAtom 은 /api/teams/mine 응답으로
  // 다시 채워질 때마다 내용이 같아도 새 객체가 되는데, 객체를 의존성으로 쓰면
  // 그때마다 트리를 다시 불러오고 drillPath 를 비운다. 그러면 시작 단계가
  // 잡히기 전에 계속 초기화돼 화면이 "로딩 중" 에서 벗어나지 못한다.
  const activeTeamId = activeTeam?.team_id ?? null;

  const loadTree = useCallback(async () => {
    if (!activeTeamId) return;
    setLoading(true);
    try {
      const r = await getTeamTree(activeTeamId);
      setTree({ categories: r.categories || [], items: r.items || [] });
    } catch (e) {
      console.warn('[TeamWizardApp] tree load:', e.message);
    } finally {
      setLoading(false);
    }
  }, [activeTeamId]);

  useEffect(() => {
    loadTree();
    // 팀이 실제로 바뀐 경우에만 선택을 버린다.
    setSelected({});
    setDrillPath([]);
  }, [activeTeamId, loadTree]);

  // 트리 관리자에서 수정이 일어나면 (TeamTreeVersionAtom +1) 자동 재로드
  useEffect(() => {
    if (treeVersion > 0) loadTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeVersion]);

  // 인덱스
  const catById = useMemo(() => {
    const m = {};
    tree.categories.forEach(c => { m[c.category_id] = c; });
    return m;
  }, [tree.categories]);

  const itemsByCategoryId = useMemo(() => {
    const m = {};
    tree.items.forEach(it => {
      if (!m[it.category_id]) m[it.category_id] = [];
      m[it.category_id].push(it);
    });
    return m;
  }, [tree.items]);

  // wizard root
  const rootCategoryId = useMemo(() => {
    const r = tree.categories.find(c => c.is_root);
    if (r) return r.category_id;
    return tree.categories[0]?.category_id || null;
  }, [tree.categories]);

  // 아직 path 가 비어 있으면 시작 단계를 path 로 친다.
  //
  // 예전에는 effect 가 drillPath 를 채워 주기를 기다렸는데, 트리를 다 받고도
  // 그 effect 가 늦거나 다시 비워지면 화면이 시작 단계를 영영 못 잡았다.
  // 시작 단계는 트리만 있으면 바로 알 수 있는 값이라 계산해서 쓴다.
  const effectivePath = drillPath.length ? drillPath : (rootCategoryId ? [rootCategoryId] : []);

  // 진행 단계 (현재 path 기반)
  const progressSteps = useMemo(() => {
    return effectivePath.map(cid => {
      const cat = catById[cid];
      if (!cat) return null;
      const sel = selected[cid];
      return { category: cat, selectedValue: sel };
    }).filter(Boolean);
  }, [effectivePath, catById, selected]);

  // 합계 계산
  const total = useMemo(() => {
    let sum = 0;
    Object.values(selected).forEach(sel => {
      if (Array.isArray(sel)) {
        sel.forEach(i => { sum += Number(i.item_price || 0); });
      } else if (sel) {
        sum += Number(sel.item_price || 0);
      }
    });
    return sum;
  }, [selected]);

  // 선택된 항목 평면 리스트 (요약용)
  const selectedItemsList = useMemo(() => {
    const list = [];
    Object.values(selected).forEach(sel => {
      if (Array.isArray(sel)) sel.forEach(i => list.push(i));
      else if (sel) list.push(sel);
    });
    return list;
  }, [selected]);

  if (!activeTeam) return null;

  const currentCategoryId = effectivePath[effectivePath.length - 1];
  const currentCat = currentCategoryId ? catById[currentCategoryId] : null;
  const currentItems = currentCategoryId ? (itemsByCategoryId[currentCategoryId] || []) : [];

  // 현재 카테고리에서 선택된 항목 (single 또는 multi)
  const currentSelection = currentCategoryId ? selected[currentCategoryId] : null;
  const isItemSelected = (item) => {
    if (!currentSelection) return false;
    if (Array.isArray(currentSelection)) {
      return currentSelection.some(i => i.item_id === item.item_id);
    }
    return currentSelection.item_id === item.item_id;
  };

  const drillInto = (nextCategoryId) => {
    if (!nextCategoryId || !catById[nextCategoryId]) return;
    // 이미 path 에 있으면 그 위치로 자르고 들어가기
    const idx = effectivePath.indexOf(nextCategoryId);
    if (idx >= 0) {
      setDrillPath(effectivePath.slice(0, idx + 1));
    } else {
      setDrillPath([...effectivePath, nextCategoryId]);
    }
  };

  const handleItemClick = (item) => {
    if (!currentCat) return;
    const isMulti = currentCat.category_type === 'M';

    if (isMulti) {
      // 토글
      const arr = Array.isArray(currentSelection) ? currentSelection : [];
      const exists = arr.find(i => i.item_id === item.item_id);
      let newArr;
      if (exists) {
        newArr = arr.filter(i => i.item_id !== item.item_id);
      } else {
        newArr = [...arr, item];
      }
      const newSelected = { ...selected };
      if (newArr.length === 0) {
        delete newSelected[currentCategoryId];
      } else {
        newSelected[currentCategoryId] = newArr;
      }
      setSelected(newSelected);
    } else {
      // 단일 선택 — 같은 항목 다시 클릭하면 해제, 다른 항목 클릭하면 교체 + drill in
      const wasSelected = currentSelection && currentSelection.item_id === item.item_id;
      const newSelected = { ...selected };
      if (wasSelected) {
        delete newSelected[currentCategoryId];
        // path 자르기 (자식 단계 제거)
        const cutPath = effectivePath.slice(0, effectivePath.indexOf(currentCategoryId) + 1);
        setDrillPath(cutPath);
      } else {
        newSelected[currentCategoryId] = item;
        // 다음 카테고리로 drill in
        if (item.next_category_id && catById[item.next_category_id]) {
          // 현재 path 다음에 next_category_id 추가 (또는 교체)
          const curIdx = effectivePath.indexOf(currentCategoryId);
          const newPath = [...effectivePath.slice(0, curIdx + 1), item.next_category_id];
          setDrillPath(newPath);
        } else {
          // 더 갈 곳이 없으면 path 그대로
        }
      }
      setSelected(newSelected);
    }
  };

  const goBack = () => {
    if (effectivePath.length > 1) {
      setDrillPath(effectivePath.slice(0, -1));
    }
  };

  const goToCrumb = (idx) => {
    setDrillPath(effectivePath.slice(0, idx + 1));
  };

  const handleResetAll = () => {
    if (!window.confirm('지금까지의 선택을 모두 초기화하시겠습니까?')) return;
    setSelected({});
    setDrillPath(rootCategoryId ? [rootCategoryId] : []);
  };

  // 빈 트리 처리
  const isEmpty = !loading && tree.categories.length === 0;

  return (
    <Page>
      <AppHeader />
      <Body>
        {/* 좌측: 진행 단계 (모바일에서는 숨김 — 고객은 항목 선택/출력만) */}
        <Column $hideOnMobile>
          <ColHeader>
            <ColTitle>
              진행 단계
              <TeamBadge>팀</TeamBadge>
            </ColTitle>
          </ColHeader>
          <ColBody>
            {isEmpty ? (
              <Empty>
                <strong style={{ color: '#1e293b' }}>{activeTeam.name}</strong> 팀의 템플릿이 비어있어요.
                <br /><br />
                헤더의 [트리 관리] 버튼으로 카테고리/항목을 먼저 추가하세요.
              </Empty>
            ) : (
              progressSteps.length === 0 ? (
                // 진짜로 불러오는 중일 때만 그렇게 말한다. 예전에는 트리를
                // 다 받고도 시작 단계를 못 잡으면 계속 "로딩 중" 이라 적어서
                // 무엇이 잘못됐는지 화면만 보고는 알 수 없었다.
                <Empty>{loading ? '로딩 중...' : '시작 단계를 찾지 못했습니다. 트리 관리에서 시작 단계를 지정해 주세요.'}</Empty>
              ) : (
                progressSteps.map((step, idx) => {
                  const isActive = step.category.category_id === currentCategoryId;
                  const isDone = !isActive && !!step.selectedValue;
                  const selLabel = (() => {
                    if (Array.isArray(step.selectedValue)) {
                      return step.selectedValue.map(i => i.item_name).join(', ');
                    }
                    return step.selectedValue?.item_name || '';
                  })();
                  return (
                    <ProgressItem
                      key={`${step.category.category_id}-${idx}`}
                      $active={isActive}
                      $done={isDone}
                      $clickable
                      onClick={() => goToCrumb(idx)}
                    >
                      <span className="num">{idx + 1}</span>
                      <div className="body">
                        <div className="title">{step.category.main_category}</div>
                        {selLabel ? (
                          <div className="selected">{selLabel}</div>
                        ) : (
                          <div className="pending">선택 중...</div>
                        )}
                      </div>
                    </ProgressItem>
                  );
                })
              )
            )}
          </ColBody>
          <ColFooter>
            <div style={{ fontSize: 11, color: '#64748b', textAlign: 'center' }}>
              팀: <strong style={{ color: '#1e293b' }}>{activeTeam.name}</strong>
              <br />
              <span style={{ fontSize: 10 }}>{activeTeam.my_role === 'owner' ? 'OWNER' : 'MEMBER'} · {activeTeam.member_count}명</span>
            </div>
          </ColFooter>
        </Column>

        {/* 중앙: drill-down wizard */}
        <Column>
          <ColHeader>
            <ColTitle>
              {currentCat ? currentCat.main_category : '템플릿'}
              <span style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>
                {currentCat ? `${currentItems.length}개 항목` : ''}
              </span>
            </ColTitle>
          </ColHeader>
          <ColBody>
            {isEmpty ? (
              <Notice>
                팀 템플릿이 비어있습니다. <strong>헤더의 [트리 관리]</strong> 버튼으로 카테고리/항목을 먼저 추가하세요.
                <br />추가하면 이 화면에서 자동으로 wizard가 진행됩니다.
              </Notice>
            ) : !currentCat ? (
              <Empty>{loading ? '로딩 중...' : '시작 단계를 찾지 못했습니다. 트리 관리에서 시작 단계를 지정해 주세요.'}</Empty>
            ) : (
              <>
                <Breadcrumb>
                  <BackBtn onClick={goBack} disabled={effectivePath.length <= 1}>← 뒤로</BackBtn>
                  {effectivePath.map((cid, idx) => {
                    const c = catById[cid];
                    if (!c) return null;
                    return (
                      <React.Fragment key={`${cid}-${idx}`}>
                        {idx > 0 && <Sep>›</Sep>}
                        <Crumb
                          $active={idx === effectivePath.length - 1}
                          onClick={() => idx !== effectivePath.length - 1 && goToCrumb(idx)}
                        >
                          {c.main_category}
                        </Crumb>
                      </React.Fragment>
                    );
                  })}
                </Breadcrumb>

                <CurrentCard>
                  <CurrentTitle>
                    {currentCat.main_category}
                    <TypeBadge $multi={currentCat.category_type === 'M'}>
                      {currentCat.category_type === 'M' ? '다중 선택' : '단일 선택'}
                    </TypeBadge>
                  </CurrentTitle>
                  {currentCat.info && <Desc>{currentCat.info}</Desc>}
                </CurrentCard>

                <SectionTitle>
                  항목 선택
                  <small>
                    · {currentCat.category_type === 'M' ? '여러 항목을 토글' : '하나를 선택하면 다음 단계로 이동'}
                  </small>
                </SectionTitle>

                {currentItems.length === 0 ? (
                  <Empty>이 카테고리에 항목이 없습니다.</Empty>
                ) : (
                  <ItemGrid>
                    {currentItems.map(item => {
                      const isSel = isItemSelected(item);
                      const hasNext = !!item.next_category_id && !!catById[item.next_category_id];
                      const nextCatName = hasNext ? catById[item.next_category_id].main_category : null;
                      const showImage = !!currentCat?.has_image;
                      return (
                        <ItemCard
                          key={item.item_id}
                          $selected={isSel}
                          onClick={() => handleItemClick(item)}
                        >
                          {isSel && <SelectionDot />}
                          {showImage && (
                            <CardImage>
                              {item.image_url ? (
                                <img src={item.image_url} alt={item.item_name} />
                              ) : (
                                <span className="placeholder">사진 없음</span>
                              )}
                            </CardImage>
                          )}
                          <ItemNameRow>
                            <ItemTitle>{item.item_name}</ItemTitle>
                            <ItemPrice>{formatNumber(item.item_price)}{priceUnit}</ItemPrice>
                          </ItemNameRow>
                          <ItemDesc>{item.item_description || '설명 없음'}</ItemDesc>
                          <ItemFooter>
                            <span>{isSel ? '선택됨' : '선택'}</span>
                            {hasNext && <NextHint>→ 다음: {nextCatName}</NextHint>}
                          </ItemFooter>
                        </ItemCard>
                      );
                    })}
                  </ItemGrid>
                )}
              </>
            )}
          </ColBody>
        </Column>

        {/* 우측: 요약 */}
        <Column>
          <ColHeader>
            <ColTitle>
              견적 요약
              <span style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>
                {selectedItemsList.length}개 선택
              </span>
            </ColTitle>
          </ColHeader>
          <ColBody>
            {selectedItemsList.length === 0 ? (
              <Empty>아직 선택된 항목이 없습니다.</Empty>
            ) : (
              selectedItemsList.map(item => (
                <SummaryRow key={item.item_id}>
                  <span className="name">{item.item_name}</span>
                  <span className="price">{formatNumber(item.item_price)}{priceUnit}</span>
                </SummaryRow>
              ))
            )}
            {selectedItemsList.length > 0 && (
              <ResetButton onClick={handleResetAll}>전체 초기화</ResetButton>
            )}
          </ColBody>
          <ColFooter>
            <TotalCard>
              <span className="label">예상 견적 합계</span>
              <span className="total">{formatNumber(total)}{priceUnit}</span>
            </TotalCard>
            <VatNote>(VAT 별도)</VatNote>
          </ColFooter>
        </Column>
      </Body>
    </Page>
  );
}
